import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import './index.less';

import { IngestionSourceModelOutput, ProjectAgentToolsMode, ProjectAgentToolsOutput } from '../../../gamma-api';
import { CheckFieldValueFnMap, useValidationForm } from '../../utils/validationUtil';
import { PUBLIC_URL } from '../../utils/configUtil';
import IbTypography from '../common/IbTypography';
import IbSwitch from '../common/IbSwitch';
import IbInput from '../common/IbInput';
import IbIcon from '../common/IbIcon';
import IbButton from '../common/IbButton';
import Switcher from '../common/Switcher';
import IbTextArea from '../common/IbTextArea';
import IbCollapse, { IbCollapsePanel } from '../common/IbCollapse';
import IbScrollSpy from '../common/IbScrollSpy';

import {
  MAIN_CLASS_NAME,
  CONTENT_CLASS_NAME,
  BLOCK_CLASS_NAME,
  BLOCK_ITEM_CLASS_NAME,
  BLOCK_TITLE_CLASS_NAME,
  FOOTER_CLASS_NAME,
} from './const';

const SHOW_URL_COPIED_TIMEOUT = 2000; // ms

const toolNameIsValid = (name: string | undefined): boolean => !!(name || '').trim();

type ProjectAgentToolsForm = {
  projectToolName: string | undefined;
  [key: string]: string | undefined;
};

export interface IProjectMCPProps {
  projectExternalId?: string;
  agentTools: ProjectAgentToolsOutput;
  sources?: IngestionSourceModelOutput[];
  onSave: (agentTools: ProjectAgentToolsOutput) => Promise<void>;
}

const ProjectMCP: React.FC<IProjectMCPProps> = ({ projectExternalId, agentTools, sources, onSave }) => {
  const { t } = useTranslation();

  const [agentToolsSettings, setAgentToolsSettings] = useState<ProjectAgentToolsOutput>(agentTools || {});
  const [saving, setSaving] = useState(false);
  const [showUrlCopied, setShowUrlCopied] = useState(false);
  const [showUrlCopiedTimer, setShowUrlCopiedTimer] = useState<NodeJS.Timeout>();

  const mcpServerUrl = new URL(`/mcps/projects/${projectExternalId}/mcp`, PUBLIC_URL).toString();

  const getSourceName = (sourceId: string) => {
    const source = (sources || []).find((s) => s.source_id == sourceId);
    return source?.name || '';
  };

  const checkFieldValueFnMap: CheckFieldValueFnMap<ProjectAgentToolsForm> = {
    projectToolName: toolNameIsValid,
  };
  for (const tool of agentTools.source_as_tool?.tools || []) {
    checkFieldValueFnMap[tool.source_id] = toolNameIsValid;
  }

  const initialValues: ProjectAgentToolsForm = {
    projectToolName: agentTools.project_as_tool?.tool?.tool_name,
  };
  for (const tool of agentTools.source_as_tool?.tools || []) {
    initialValues[tool.source_id] = tool.tool_name;
  }

  const {
    validationState,
    onFieldChange,
    onFieldBlur,
    validate,
    reset,
    getShowError,
    getErrorMessage,
  } = useValidationForm<ProjectAgentToolsForm>(initialValues, checkFieldValueFnMap);

  const onMCPServerEnabledChange = () => {
    setAgentToolsSettings({
      ...agentToolsSettings,
      mcp_server_enabled: !agentToolsSettings?.mcp_server_enabled,
      mode: agentToolsSettings.mode ? agentToolsSettings.mode : ProjectAgentToolsMode.ProjectAsTool,
    } as ProjectAgentToolsOutput);
  };

  const onAgentToolsModeChange = (value: string) => {
    setAgentToolsSettings({
      ...agentToolsSettings,
      mode: value as ProjectAgentToolsMode,
    } as ProjectAgentToolsOutput);
  };

  const onProjectAsToolToolNameChange = (value: string) => {
    setAgentToolsSettings({
      ...agentToolsSettings,
      project_as_tool: {
        ...agentToolsSettings.project_as_tool,
        tool: {
          ...agentToolsSettings.project_as_tool?.tool,
          tool_name: value,
        },
      },
    } as ProjectAgentToolsOutput);
    onFieldChange('projectToolName', value);
  };

  const onProjectAsToolToolNameBlur = () => onFieldBlur('projectToolName');

  const onProjectAsToolToolDescriptionChange = (value: string) => {
    setAgentToolsSettings({
      ...agentToolsSettings,
      project_as_tool: {
        ...agentToolsSettings.project_as_tool,
        tool: {
          ...agentToolsSettings.project_as_tool?.tool,
          tool_description: value,
        },
      },
    } as ProjectAgentToolsOutput);
  };

  const onSourceAsToolToolNameChange = (source_id: string) => (value: string) => {
    setAgentToolsSettings({
      ...agentToolsSettings,
      source_as_tool: {
        tools: (agentToolsSettings.source_as_tool?.tools || []).map((tool) => ({
          ...tool,
          tool_name: tool.source_id == source_id ? value : tool.tool_name,
        })),
      },
    } as ProjectAgentToolsOutput);
    onFieldChange(source_id, value);
  };

  const onSourceAsToolToolNameBlur = (source_id: string) => () => onFieldBlur(source_id);

  const onSourceAsToolToolDescriptionChange = (source_id: string) => (value: string) => {
    setAgentToolsSettings({
      ...agentToolsSettings,
      source_as_tool: {
        tools: (agentToolsSettings.source_as_tool?.tools || []).map((tool) => ({
          ...tool,
          tool_description: tool.source_id == source_id ? value : tool.tool_description,
        })),
      },
    } as ProjectAgentToolsOutput);
  };

  const onSaveButtonClick = async () => {
    if (!validate()) {
      return;
    }

    setSaving(true);
    await onSave(agentToolsSettings);
    setSaving(false);
  };

  const onCopyUrlClick = () => {
    navigator.clipboard.writeText(mcpServerUrl);
    setShowUrlCopied(true);
  };

  const onAgentToolsPropChange = () => {
    setAgentToolsSettings(agentTools || {});
    reset(initialValues);
  };
  useEffect(onAgentToolsPropChange, [agentTools]);

  const onShowUrlCopiedChange = () => {
    if (showUrlCopied) {
      !!showUrlCopiedTimer && clearTimeout(showUrlCopiedTimer);
      setShowUrlCopiedTimer(setTimeout(() => setShowUrlCopied(false), SHOW_URL_COPIED_TIMEOUT));
    }
  };
  useEffect(onShowUrlCopiedChange, [showUrlCopied]);

  const renderMCPBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Project agent tools: MCP server')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography>
          <IbSwitch checked={agentToolsSettings?.mcp_server_enabled} size="small" onClick={onMCPServerEnabledChange} />
          <IbTypography.Paragraph strong>{t('Project agent tools: Enable')}</IbTypography.Paragraph>
        </IbTypography>
      </div>
      {agentToolsSettings?.mcp_server_enabled && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Project agent tools: MCP server url')}
            </IbTypography.Paragraph>
            <IbInput value={mcpServerUrl} />
            {showUrlCopied ? (
              <IbButton icon={<IbIcon iconName="check" />} status="success" type="link">
                {t('Project agent tools: Copied')}
              </IbButton>
            ) : (
              <IbButton icon={<IbIcon iconName="copy-one" />} type="link" onClick={onCopyUrlClick}>
                {t('Project agent tools: Copy')}
              </IbButton>
            )}
          </div>
        </>
      )}
    </div>
  );

  const renderToolsBlock = () => (
    <div className={BLOCK_CLASS_NAME}>
      <IbTypography className={BLOCK_TITLE_CLASS_NAME}>
        <h4>{t('Project agent tools: Tools')}</h4>
      </IbTypography>
      <div className={BLOCK_ITEM_CLASS_NAME}>
        <IbTypography.Paragraph strong type="secondary">
          {t('Project agent tools: Mode')}
        </IbTypography.Paragraph>
        <Switcher
          options={[
            { value: ProjectAgentToolsMode.ProjectAsTool, label: t('Project agent tools: Single tool') },
            { value: ProjectAgentToolsMode.SourceAsTool, label: t('Project agent tools: Tool for every source') },
          ]}
          value={agentToolsSettings?.mode}
          onChange={onAgentToolsModeChange}
        />
      </div>
      {agentToolsSettings.mode === ProjectAgentToolsMode.ProjectAsTool && (
        <>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Project agent tools: Tool name')}
            </IbTypography.Paragraph>
            <IbInput
              status={getShowError(validationState.projectToolName) ? 'error' : 'default'}
              value={agentToolsSettings.project_as_tool?.tool?.tool_name}
              onBlur={onProjectAsToolToolNameBlur}
              onChange={onProjectAsToolToolNameChange}
            />
            {getShowError(validationState.projectToolName) && (
              <IbTypography.Paragraph error type="descriptor">
                {getErrorMessage(validationState.projectToolName, t('Project agent tools: Tool name is not valid'))}
              </IbTypography.Paragraph>
            )}
          </div>
          <div className={BLOCK_ITEM_CLASS_NAME}>
            <IbTypography.Paragraph strong type="secondary">
              {t('Project agent tools: Tool description')}
            </IbTypography.Paragraph>
            <IbTextArea
              value={agentToolsSettings.project_as_tool?.tool?.tool_description}
              onChange={onProjectAsToolToolDescriptionChange}
            />
          </div>
        </>
      )}
      {agentToolsSettings.mode === ProjectAgentToolsMode.SourceAsTool && (
        <div className={BLOCK_ITEM_CLASS_NAME}>
          <IbCollapse
            accordion={false}
            activeKey={agentToolsSettings.source_as_tool?.tools?.map((t) => t.source_id)}
            expandIcon={() => null}
          >
            {agentToolsSettings.source_as_tool?.tools?.map((tool) => (
              <IbCollapsePanel key={tool.source_id} header={getSourceName(tool.source_id)}>
                <div className={BLOCK_ITEM_CLASS_NAME}>
                  <IbTypography.Paragraph strong type="secondary">
                    {t('Project agent tools: Tool name')}
                  </IbTypography.Paragraph>
                  <IbInput
                    status={getShowError(validationState[tool.source_id]) ? 'error' : 'default'}
                    value={tool.tool_name}
                    onBlur={onSourceAsToolToolNameBlur(tool.source_id)}
                    onChange={onSourceAsToolToolNameChange(tool.source_id)}
                  />
                  {getShowError(validationState[tool.source_id]) && (
                    <IbTypography.Paragraph error type="descriptor">
                      {getErrorMessage(
                        validationState[tool.source_id],
                        t('Project agent tools: Tool name is not valid')
                      )}
                    </IbTypography.Paragraph>
                  )}
                </div>
                <div className={BLOCK_ITEM_CLASS_NAME}>
                  <IbTypography.Paragraph strong type="secondary">
                    {t('Project agent tools: Tool description')}
                  </IbTypography.Paragraph>
                  <IbTextArea
                    value={tool.tool_description}
                    onChange={onSourceAsToolToolDescriptionChange(tool.source_id)}
                  />
                </div>
              </IbCollapsePanel>
            ))}
          </IbCollapse>
        </div>
      )}
    </div>
  );

  const sections = [
    { id: 'mcp', title: t('Project agent tools: MCP server'), content: renderMCPBlock() },
    { id: 'tools', title: t('Project agent tools: Tools'), content: renderToolsBlock() },
  ];

  return (
    <div className={MAIN_CLASS_NAME}>
      <div className={CONTENT_CLASS_NAME}>
        <IbScrollSpy displayNav sections={sections} />
      </div>
      <div className={FOOTER_CLASS_NAME}>
        <IbButton disabled={saving} onClick={onSaveButtonClick}>
          {t('Project agent tools: Save')}
        </IbButton>
      </div>
    </div>
  );
};

export default ProjectMCP;
